import React, { useState, useEffect } from 'react';
import { auditAPI, utentiAPI } from '../services/api';
import '../styles/RegistroAccessi.css';

/**
 * RegistroModifiche — schermata riservata agli admin.
 *
 * Mostra chi ha cambiato cosa e permette di annullare una modifica.
 *
 * L'annullamento e' esplicito su COSA tornera' indietro: non un
 * generico "annulla" ma "riporta telefono a 3474823797". Un pulsante
 * che non dice cosa fa, su dati clinici, e' una trappola.
 *
 * Il database rifiuta comunque l'annullamento se il campo e' stato
 * cambiato ancora dopo: la salvaguardia non dipende da questa pagina.
 */

const messaggioErrore = (e, ripiego) => e?.message || e?.response?.data?.message || ripiego;

const dataOra = (v) =>
    v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const ETICHETTE_OPERAZIONE = {
    INSERT: 'Creazione',
    UPDATE: 'Modifica',
    DELETE: 'Cancellazione',
};

const FILTRI_VUOTI = { tabella: '', record_id: '', utente_id: '', operazione: '', dal: '', al: '' };

/** Valore leggibile: i vuoti vanno detti, non lasciati bianchi. */
const mostraValore = (v) => {
    if (v === null || v === undefined || v === '') return '(vuoto)';
    if (typeof v === 'boolean') return v ? 'sì' : 'no';
    const s = String(v);
    return s.length > 80 ? s.slice(0, 80) + '…' : s;
};

const RegistroModifiche = () => {
    const [modifiche, setModifiche] = useState([]);
    const [tabelle, setTabelle] = useState({});
    const [utenti, setUtenti] = useState([]);
    const [filtri, setFiltri] = useState(FILTRI_VUOTI);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [inCorso, setInCorso] = useState(null);

    const carica = async (f = filtri) => {
        try {
            setCaricamento(true);
            setErrore(null);
            const puliti = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
            const r = await auditAPI.modifiche(puliti);
            setModifiche(r.data?.data?.modifiche || []);
            setTabelle(r.data?.data?.tabelle || {});
        } catch (e) {
            setErrore(messaggioErrore(e, 'Impossibile caricare il registro'));
        } finally {
            setCaricamento(false);
        }
    };

    useEffect(() => {
        carica(FILTRI_VUOTI);
        utentiAPI.getAll()
            .then((r) => setUtenti(r.data?.data?.utenti || []))
            .catch(() => {});
        // eslint-disable-next-line
    }, []);

    const aggiorna = (campo, valore) => setFiltri((p) => ({ ...p, [campo]: valore }));

    const azzera = () => {
        setFiltri(FILTRI_VUOTI);
        carica(FILTRI_VUOTI);
    };

    const campiDi = (m) => {
        if (!m.cambiamenti) return [];
        return Object.entries(m.cambiamenti);
    };

    const ripristina = async (m) => {
        const campi = campiDi(m)
            .map(([campo, v]) => `  ${campo} → ${mostraValore(v.prima)}`)
            .join('\n');

        if (!window.confirm(`Riportare indietro questi campi?\n\n${campi}\n\nL'operazione resta registrata.`)) return;

        setErrore(null);
        setAvviso(null);
        try {
            setInCorso(m.id);
            await auditAPI.ripristinaModifica(m.id);
            setAvviso('Modifica annullata.');
            carica();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nell\'annullamento'));
        } finally {
            setInCorso(null);
        }
    };

    return (
        <div className="ra-wrap">
            <div className="ra-testa">
                <div>
                    <h1>Registro modifiche</h1>
                    <p className="ra-sottotitolo">
                        Chi ha cambiato cosa, con il valore precedente. Le modifiche possono
                        essere annullate, purché nessuno sia intervenuto nel frattempo sullo
                        stesso campo.
                    </p>
                </div>
            </div>

            <div className="ra-filtri">
                <div className="ra-campo">
                    <label htmlFor="tabella">Tipo di dato</label>
                    <select id="tabella" value={filtri.tabella}
                            onChange={(e) => aggiorna('tabella', e.target.value)}>
                        <option value="">tutti</option>
                        {Object.entries(tabelle).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="record_id">Id record</label>
                    <input id="record_id" type="number" value={filtri.record_id}
                           onChange={(e) => aggiorna('record_id', e.target.value)}
                           placeholder="es. 10" />
                </div>

                <div className="ra-campo">
                    <label htmlFor="utente_id">Utente</label>
                    <select id="utente_id" value={filtri.utente_id}
                            onChange={(e) => aggiorna('utente_id', e.target.value)}>
                        <option value="">tutti</option>
                        {utenti.map((u) => (
                            <option key={u.id} value={u.id}>{u.cognome} {u.nome}</option>
                        ))}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="operazione">Operazione</label>
                    <select id="operazione" value={filtri.operazione}
                            onChange={(e) => aggiorna('operazione', e.target.value)}>
                        <option value="">tutte</option>
                        {Object.entries(ETICHETTE_OPERAZIONE).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="dal">Dal</label>
                    <input id="dal" type="date" value={filtri.dal}
                           onChange={(e) => aggiorna('dal', e.target.value)} />
                </div>

                <div className="ra-campo">
                    <label htmlFor="al">Al</label>
                    <input id="al" type="date" value={filtri.al}
                           onChange={(e) => aggiorna('al', e.target.value)} />
                </div>

                <div className="ra-filtri-azioni">
                    <button className="ra-btn-secondario" onClick={azzera}>Azzera</button>
                    <button className="ra-btn-primario" onClick={() => carica()} disabled={caricamento}>
                        {caricamento ? 'Ricerca…' : 'Cerca'}
                    </button>
                </div>
            </div>

            {errore && <div className="ra-errore">{errore}</div>}
            {avviso && <div className="ra-avviso">{avviso}</div>}

            {caricamento ? (
                <div className="ra-stato">Caricamento…</div>
            ) : modifiche.length === 0 ? (
                <div className="ra-vuoto">Nessuna modifica corrisponde ai filtri.</div>
            ) : (
                <div className="rm-elenco">
                    {modifiche.map((m) => {
                        const campi = campiDi(m);
                        const annullabile = m.operazione === 'UPDATE' && !m.ripristinato_il && campi.length > 0;

                        return (
                            <div key={m.id} className={`rm-voce ${m.ripristinato_il ? 'rm-voce-annullata' : ''}`}>
                                <div className="rm-voce-testa">
                                    <div>
                                        <span className={`ra-azione ra-azione-${m.operazione.toLowerCase()}`}>
                                            {ETICHETTE_OPERAZIONE[m.operazione] || m.operazione}
                                        </span>
                                        <span className="rm-oggetto">
                                            {tabelle[m.tabella] || m.tabella}
                                            {m.record_id ? ` #${m.record_id}` : ''}
                                        </span>
                                    </div>
                                    <span className="ra-quando">{dataOra(m.created_at)}</span>
                                </div>

                                <div className="rm-autore">
                                    {m.utente_nome || <span className="ra-ignoto">modifica non attribuita a un utente</span>}
                                </div>

                                {campi.length > 0 && (
                                    <table className="rm-campi">
                                        <tbody>
                                            {campi.map(([campo, v]) => (
                                                <tr key={campo}>
                                                    <td className="rm-campo-nome">{campo}</td>
                                                    <td className="rm-campo-prima">{mostraValore(v.prima)}</td>
                                                    <td className="rm-freccia">→</td>
                                                    <td className="rm-campo-dopo">{mostraValore(v.dopo)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}

                                {m.operazione === 'DELETE' && (
                                    <p className="rm-nota">
                                        Cancellazione: il ripristino avviene dal cestino, non da qui.
                                    </p>
                                )}

                                <div className="rm-voce-piede">
                                    {m.ripristinato_il ? (
                                        <span className="rm-annullata">
                                            Annullata il {dataOra(m.ripristinato_il)}
                                            {m.ripristinato_da_cognome ? ` da ${m.ripristinato_da_cognome}` : ''}
                                        </span>
                                    ) : annullabile ? (
                                        <button
                                            className="ra-btn-secondario"
                                            onClick={() => ripristina(m)}
                                            disabled={inCorso === m.id}
                                        >
                                            {inCorso === m.id
                                                ? 'Annullamento…'
                                                : `Riporta ${campi.map(([c]) => c).join(', ')} al valore precedente`}
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <p className="ra-piede">
                Il registro mostra le ultime 100 righe corrispondenti ai filtri. Anche gli
                annullamenti vengono registrati: la storia delle modifiche non viene mai riscritta.
            </p>
        </div>
    );
};

export default RegistroModifiche;
